import React from 'react';
import type { EvidenceItem } from '../../types/evidence';
import evidenceAPI from '../../services/evidenceAPI';
import { FileText, Calendar, CheckCircle, AlertTriangle, XCircle, Clock } from 'lucide-react';

interface EvidenceItemCardProps {
  evidence: EvidenceItem;
  onClick?: (evidence: EvidenceItem) => void;
}

const EvidenceItemCard: React.FC<EvidenceItemCardProps> = ({ evidence, onClick }) => {
  const getValidationIcon = () => {
    switch (evidence.validation_status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'needs_review':
        return <AlertTriangle className="h-4 w-4 text-orange-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStalenessIcon = () => {
    switch (evidence.staleness_status) {
      case 'current':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'approaching_stale':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'stale':
        return <AlertTriangle className="h-4 w-4 text-orange-600" />;
      case 'expired':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <div
      onClick={() => onClick && onClick(evidence)}
      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-blue-300 transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          <FileText className="h-5 w-5 text-gray-400 mt-1" />
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900 truncate">
              {evidence.evidence_name}
            </h4>
            <p className="text-xs text-gray-500 mt-1">ID: {evidence.evidence_id}</p>
            {(evidence.evidence_data?.summary || evidence.evidence_description) && (
              <p className="text-xs text-gray-600 mt-2 line-clamp-2">
                {typeof evidence.evidence_data?.summary === 'string'
                  ? evidence.evidence_data.summary
                  : evidence.evidence_description}
                {' • '}
                <span className="text-gray-500">
                  Last updated: {(() => {
                    const date = new Date(evidence.collection_date);
                    const now = new Date();
                    const diff = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
                    if (diff < 1) return 'just now';
                    if (diff < 24) return `${diff} hour${diff !== 1 ? 's' : ''} ago`;
                    const days = Math.floor(diff / 24);
                    return `${days} day${days !== 1 ? 's' : ''} ago`;
                  })()}
                </span>
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2 ml-2">
          {getValidationIcon()}
          {getStalenessIcon()}
        </div>
      </div>

      {/* Metadata */}
      <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
        <div className="flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          <span>{formatDate(evidence.evidence_date)}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="font-medium">{evidence.evidence_type}</span>
        </div>
      </div>

      {/* Status Badges */}
      <div className="flex flex-wrap gap-2">
        <span className={`px-2 py-1 text-xs font-medium rounded ${evidenceAPI.getStatusColor(evidence.validation_status)}`}>
          {evidence.validation_status}
        </span>
        <span className={`px-2 py-1 text-xs font-medium rounded ${evidenceAPI.getStatusColor(evidence.staleness_status)}`}>
          {evidence.staleness_status}
        </span>
        {evidence.source && (
          <span className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded">
            {evidence.source.source_name}
          </span>
        )}
      </div>

      {/* Compliance Tags */}
      {evidence.compliance_tags && evidence.compliance_tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {evidence.compliance_tags.slice(0, 3).map((tag, idx) => (
            <span
              key={idx}
              className="px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-50 rounded"
            >
              {tag}
            </span>
          ))}
          {evidence.compliance_tags.length > 3 && (
            <span className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-50 rounded">
              +{evidence.compliance_tags.length - 3}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default EvidenceItemCard;
